'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  Button,
  IconButton,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { AdGraphNodeDto } from '@terra/shared-contracts-internal-network';
import { InternalNetworkAdEntityKindSchema } from '@terra/shared-schemas-orchestrator';
import {
  ENTITY_KIND_COLOR_MAP,
  type InternalNetworkAdEntityKind,
} from './constants';
import { InternalNetworkGraphPopoverCard } from './InternalNetworkGraphPopoverCard';

type InternalNetworkGraphNodePopoverProps = {
  node: AdGraphNodeDto;
  isExpanding: boolean;
  onExpand: () => void;
  onViewDetails: () => void;
  onClose: () => void;
};

export function InternalNetworkGraphNodePopover({
  node,
  isExpanding,
  onExpand,
  onViewDetails,
  onClose,
}: InternalNetworkGraphNodePopoverProps) {
  const t = useTranslations('internalNetwork.graph.explore');
  const tInternalNetwork = useTranslations('internalNetwork');
  const parsedKind = InternalNetworkAdEntityKindSchema.safeParse(node.kind);
  const entityKind = parsedKind.success ? parsedKind.data : null;
  const entityKindLabel = entityKind
    ? tInternalNetwork(
        `crownJewels.entityKinds.${entityKind}` as Parameters<
          typeof tInternalNetwork
        >[0],
      )
    : node.kind;
  const showObjectId =
    node.label.trim().toLowerCase() !== node.objectId.trim().toLowerCase();

  return (
    <InternalNetworkGraphPopoverCard
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.stopPropagation()}
    >
      <VStack gap={0} alignItems="stretch">
        <HStack
          justifyContent="space-between"
          alignItems="center"
          px={3}
          pt={3}
          pb={2}
        >
          {entityKind ? (
            <Badge
              variant="outline"
              size="sm"
              colorPalette={
                ENTITY_KIND_COLOR_MAP[entityKind as InternalNetworkAdEntityKind]
              }
            >
              {entityKindLabel}
            </Badge>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              {entityKindLabel}
            </Text>
          )}
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={t('clearSelection')}
            onClick={onClose}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>

        <Box px={3} pb={3}>
          <Tooltip title={node.label}>
            <Text fontSize="sm" fontWeight="medium" truncate>
              {node.label}
            </Text>
          </Tooltip>
          {node.samAccountName &&
          node.samAccountName.toLowerCase() !== node.label.toLowerCase() ? (
            <Text fontSize="xs" color="fg.muted" fontFamily="mono" truncate>
              {node.samAccountName}
            </Text>
          ) : null}
          {showObjectId ? (
            <Text fontSize="xs" color="fg.muted" fontFamily="mono" truncate>
              {node.objectId}
            </Text>
          ) : null}
        </Box>

        <HStack
          gap={0}
          borderTopWidth="1px"
          borderTopColor="border.neutral.subtle"
        >
          <Button
            size="xs"
            variant="ghost"
            flex={1}
            borderRadius="0 0 0 md"
            loading={isExpanding}
            onClick={onExpand}
          >
            {t('expandNode')}
          </Button>
          <Box
            w="1px"
            backgroundColor="border.neutral.subtle"
            alignSelf="stretch"
          />
          <Button
            size="xs"
            variant="ghost"
            flex={1}
            borderRadius="0 0 md 0"
            onClick={onViewDetails}
          >
            {t('viewDetails')}
          </Button>
        </HStack>
      </VStack>
    </InternalNetworkGraphPopoverCard>
  );
}
